import { useMemo, useState, useCallback } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import { isValidProjectId } from '../../utils/projectIds';
import CommunityShareModal from './CommunityShareModal';
import './MarketingStudioPage.css';

/* ── Constants ── */
const TABS = ['overview', 'copy-studio', 'visual-studio', 'asset-library', 'compose'];
const TAB_CONFIG = {
  overview: { labelKey: 'marketing.tabs.overview', icon: '📊' },
  'copy-studio': { labelKey: 'marketing.tabs.copyStudio', icon: '✍️' },
  'visual-studio': { labelKey: 'marketing.tabs.visualStudio', icon: '🎨' },
  'asset-library': { labelKey: 'marketing.tabs.assetLibrary', icon: '📁' },
  compose: { labelKey: 'marketing.compose', icon: '🧩' },
};

const COPY_TYPES = [
  { key: 'instagram', value: 'Instagram', labelKey: 'marketing.copyTypes.instagram', icon: '📸' },
  { key: 'twitter', value: 'Twitter/X', labelKey: 'marketing.copyTypes.twitter', icon: 'X' },
  { key: 'email', value: 'Email', labelKey: 'marketing.copyTypes.email', icon: '📧' },
  { key: 'back-cover', value: 'Back Cover', labelKey: 'marketing.copyTypes.backCover', icon: '📖' },
  { key: 'ad-copy', value: 'Ad Copy', labelKey: 'marketing.copyTypes.adCopy', icon: '📢' },
  { key: 'press-release', value: 'Press Release', labelKey: 'marketing.copyTypes.pressRelease', icon: '📰' },
  { key: 'blog-intro', value: 'Blog Intro', labelKey: 'marketing.copyTypes.blogIntro', icon: '🖊' },
  { key: 'tagline', value: 'Tagline', labelKey: 'marketing.copyTypes.tagline', icon: '💎' },
];
const TONE_OPTIONS = [
  { value: 'Mysterious', labelKey: 'marketing.toneOptions.mysterious' },
  { value: 'Energetic', labelKey: 'marketing.toneOptions.energetic' },
  { value: 'Subtle', labelKey: 'marketing.toneOptions.subtle' },
  { value: 'Professional', labelKey: 'marketing.toneOptions.professional' },
  { value: 'Playful', labelKey: 'marketing.toneOptions.playful' },
  { value: 'Dramatic', labelKey: 'marketing.toneOptions.dramatic' },
  { value: 'Heartfelt', labelKey: 'marketing.toneOptions.heartfelt' },
  { value: 'Bold', labelKey: 'marketing.toneOptions.bold' },
];
const CAMPAIGN_OPTIONS = [
  { value: 'All', labelKey: 'marketing.campaignOptions.all' },
  { value: 'Launch', labelKey: 'marketing.campaignOptions.launch' },
  { value: 'Teasers', labelKey: 'marketing.campaignOptions.teasers' },
  { value: 'Reviews', labelKey: 'marketing.campaignOptions.reviews' },
  { value: 'Ads', labelKey: 'marketing.campaignOptions.ads' },
];
const PREVIEW_TABS = [
  { key: 'ig', labelKey: 'marketing.previewTabs.ig', icon: '📸' },
  { key: 'tw', labelKey: 'marketing.previewTabs.tw', icon: '' },
  { key: 'email', labelKey: 'marketing.previewTabs.emailTab', icon: '📧' },
];

const ART_STYLES = [
  { key: 'cinematic', value: 'Cinematic', labelKey: 'marketing.artStyles.cinematic', descKey: 'marketing.artStyles.cinematicDesc' },
  { key: 'illustrated', value: 'Illustrated', labelKey: 'marketing.artStyles.illustrated', descKey: 'marketing.artStyles.illustratedDesc' },
  { key: 'watercolor', value: 'Watercolor', labelKey: 'marketing.artStyles.watercolor', descKey: 'marketing.artStyles.watercolorDesc' },
  { key: 'minimalist', value: 'Minimalist', labelKey: 'marketing.artStyles.minimalist', descKey: 'marketing.artStyles.minimalistDesc' },
  { key: 'dark-fantasy', value: 'Dark Fantasy', labelKey: 'marketing.artStyles.darkFantasy', descKey: 'marketing.artStyles.darkFantasyDesc' },
  { key: 'photorealistic', value: 'Photorealistic', labelKey: 'marketing.artStyles.photorealistic', descKey: 'marketing.artStyles.photorealisticDesc' },
];

const ASPECT_RATIOS = [
  { key: '1:1', label: '1:1', descKey: 'marketing.aspectRatios.square' },
  { key: '9:16', label: '9:16', descKey: 'marketing.aspectRatios.portrait' },
  { key: '16:9', label: '16:9', descKey: 'marketing.aspectRatios.landscape' },
  { key: '3:4', label: '3:4', descKey: 'marketing.aspectRatios.bookCover' },
];

/* Helper: friendly label for file paths */
const friendlyContextLabel = (path, t) => {
  if (path === 'Notes/story-bible.md') return t('marketing.storyBibleLabel');
  const parts = path.split('/');
  const folder = parts[0];
  const filename = parts[parts.length - 1].replace(/\.md$/, '').replace(/-/g, ' ');
  const capitalize = (s) => s.replace(/\b\w/g, (c) => c.toUpperCase());
  if (folder === 'Chapters') return `${t('sidebar.systemFolders.chapters')}: ${capitalize(filename)}`;
  if (folder === 'Characters') return capitalize(filename);
  if (folder === 'World Building') return `${t('sidebar.systemFolders.worldBuilding')}: ${capitalize(filename)}`;
  return capitalize(filename);
};

/* Helper: extract category icon for context */
const contextIcon = (path) => {
  if (path.startsWith('Chapters/')) return '\u00B6';
  if (path.startsWith('Characters/')) return '\u2666';
  if (path.startsWith('World Building/')) return '\u2609';
  if (path === 'Notes/story-bible.md') return '\u2756';
  return '\u2022';
};


/* ═══════ Component ═══════ */
function MarketingStudioPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const safeProjectId = isValidProjectId(projectId) ? projectId : null;

  const project = useQuery(api.projects.getById, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const files = useQuery(api.files.listByProject, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const storyBibleEntries = useQuery(api.storyBibleEntries.list, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const generateVisualImageAction = useAction(api.ai.generateVisualImage);
  const generateMarketingCopyAction = useAction(api.ai.generateMarketingCopy);

  // Convex-persisted marketing assets
  const persistedAssets = useQuery(api.marketingAssets.listByProject, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const saveCopyAssetMutation = useMutation(api.marketingAssets.saveCopyAsset);
  const saveVisualAssetMutation = useMutation(api.marketingAssets.saveVisualAsset);
  const updateCopyContentMutation = useMutation(api.marketingAssets.updateCopyContent);

  // Derive saved assets from Convex
  const savedCopyAssets = useMemo(() => {
    if (!persistedAssets) return [];
    return persistedAssets
      .filter((a) => a.assetType === 'copy')
      .map((a) => ({ ...a, id: a._id, savedAt: a.createdAt }));
  }, [persistedAssets]);

  const generatedImages = useMemo(() => {
    if (!persistedAssets) return [];
    return persistedAssets
      .filter((a) => a.assetType === 'visual')
      .map((a) => ({ ...a, id: a._id, url: a.imageUrl, timestamp: a.createdAt }));
  }, [persistedAssets]);

  /* State */
  const [activeTab, setActiveTab] = useState('overview');
  const [isGeneratingCopy, setIsGeneratingCopy] = useState(false);
  const [isGeneratingVisual, setIsGeneratingVisual] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Copy Studio state
  const [copyType, setCopyType] = useState('instagram');
  const [copyTone, setCopyTone] = useState('Mysterious');
  const [copyCampaign, setCopyCampaign] = useState('Launch');
  const [copyExtraInstructions, setCopyExtraInstructions] = useState('');
  const [generatedVariants, setGeneratedVariants] = useState([]);
  const [previewTab, setPreviewTab] = useState('ig');
  const [selectedVariant, setSelectedVariant] = useState(null);

  // Visual Studio state
  const [genEngine, setGenEngine] = useState('quick');
  const [artStyle, setArtStyle] = useState('cinematic');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [storyContext, setStoryContext] = useState('');
  const [visualPrompt, setVisualPrompt] = useState('');
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [expandedImage, setExpandedImage] = useState(null);

  // Community share state
  const [shareModal, setShareModal] = useState(null); // { body, imageStorageId, audioStorageId }

  // Compose tab state
  const [composeSelectedCopy, setComposeSelectedCopy] = useState(null);
  const [composeSelectedVisual, setComposeSelectedVisual] = useState(null);
  const [composePreviewTab, setComposePreviewTab] = useState('ig');

  // Asset Library state
  const [libraryTypeFilter, setLibraryTypeFilter] = useState('all');
  const [libraryCampaignFilter, setLibraryCampaignFilter] = useState('All');
  const [libraryViewMode, setLibraryViewMode] = useState('grid');
  const [editingCopyId, setEditingCopyId] = useState(null);
  const [editingCopyText, setEditingCopyText] = useState('');

  /* Story context options: chapters, characters, story bible, world building */
  const storyContextOptions = useMemo(() => {
    if (!files) return [];
    const grouped = { chapters: [], characters: [], world: [], bible: [] };
    for (const f of files) {
      if (f.type !== 'file' || !f.content?.trim()) continue;
      const opt = { path: f.path, label: friendlyContextLabel(f.path, t), icon: contextIcon(f.path), preview: f.content.slice(0, 120) };
      if (f.path.startsWith('Chapters/')) grouped.chapters.push(opt);
      else if (f.path.startsWith('Characters/')) grouped.characters.push(opt);
      else if (f.path.startsWith('World Building/')) grouped.world.push(opt);
      else if (f.path === 'Notes/story-bible.md') grouped.bible.push(opt);
    }
    return [...grouped.bible, ...grouped.characters, ...grouped.chapters, ...grouped.world];
  }, [files, t]);

  const selectedContextContent = useMemo(() => {
    if (!storyContext || !files) return '';
    const match = files.find((f) => f.path === storyContext);
    return match?.content || '';
  }, [storyContext, files]);

  /* Auto-gather story bible, character summaries, and world building */
  const storyBibleContent = useMemo(() => {
    if (!files) return '';
    const bible = files.find((f) => f.path === 'Notes/story-bible.md');
    return bible?.content || '';
  }, [files]);

  const characterSummaries = useMemo(() => {
    if (!files) return '';
    return files
      .filter((f) => f.type === 'file' && f.path.startsWith('Characters/') && f.path !== 'Characters/' && f.content?.trim())
      .map((f) => {
        const name = f.name.replace(/\.md$/, '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        return `## ${name}\n${f.content.slice(0, 500)}`;
      })
      .join('\n\n');
  }, [files]);

  const worldBuildingContent = useMemo(() => {
    if (!files) return '';
    return files
      .filter((f) => f.type === 'file' && f.path.startsWith('World Building/') && f.path !== 'World Building/' && f.content?.trim())
      .map((f) => {
        const name = f.name.replace(/\.md$/, '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        return `## ${name}\n${f.content.slice(0, 500)}`;
      })
      .join('\n\n');
  }, [files]);

  /* Character extraction: prefer structured story bible entries, fall back to files */
  const characters = useMemo(() => {
    const charMap = new Map();

    // Priority 1: Story Bible v2 entries (characters category)
    if (storyBibleEntries?.length > 0) {
      for (const entry of storyBibleEntries) {
        if (entry.category === 'character') {
          const desc = [
            entry.description,
            ...(entry.quickFacts || []).map((f) => `${f.key}: ${f.value}`),
          ].filter(Boolean).join('. ');
          charMap.set(entry.name, { name: entry.name, description: desc.slice(0, 400), source: 'story-bible' });
        }
      }
    }

    // Priority 2: Character files (if no story bible entries)
    if (charMap.size === 0 && files) {
      for (const f of files) {
        if (f.type !== 'file' || !f.content?.trim()) continue;
        if (f.path.startsWith('Characters/') && f.path !== 'Characters/') {
          const name = f.name.replace(/\.md$/, '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
          if (!charMap.has(name)) {
            charMap.set(name, { name, description: f.content.slice(0, 300), source: 'file' });
          }
        }
      }
      // Also check legacy story bible headers
      const bible = files.find((f) => f.path === 'Notes/story-bible.md');
      if (bible?.content) {
        const headerRegex = /^###\s+(.+)$/gm;
        let match;
        while ((match = headerRegex.exec(bible.content)) !== null) {
          const name = match[1].trim();
          if (!charMap.has(name)) {
            const startIdx = match.index + match[0].length;
            const nextHeader = bible.content.indexOf('\n###', startIdx);
            const desc = bible.content.slice(startIdx, nextHeader === -1 ? startIdx + 300 : nextHeader).trim().slice(0, 300);
            charMap.set(name, { name, description: desc, source: 'bible' });
          }
        }
      }
    }

    return Array.from(charMap.values()).slice(0, 20);
  }, [files, storyBibleEntries]);

  const toggleCharacter = useCallback((char) => {
    setSelectedCharacters((prev) => {
      const exists = prev.find((c) => c.name === char.name);
      if (exists) return prev.filter((c) => c.name !== char.name);
      return [...prev, char];
    });
  }, []);

  const localizeCopyType = useCallback((value) => {
    const match = COPY_TYPES.find((type) => type.value === value);
    return match ? t(match.labelKey) : value;
  }, [t]);

  const localizeTone = useCallback((value) => {
    const match = TONE_OPTIONS.find((tone) => tone.value === value);
    return match ? t(match.labelKey) : value;
  }, [t]);

  const localizeArtStyle = useCallback((value) => {
    const match = ART_STYLES.find((style) => style.value === value);
    return match ? t(match.labelKey) : value;
  }, [t]);

  /* Handlers */
  const generateCopyVariants = async () => {
    if (!safeProjectId || isGeneratingCopy) return;
    setIsGeneratingCopy(true);
    setErrorMessage('');
    setSuccessMessage('');
    setGeneratedVariants([]);
    setSelectedVariant(null);
    try {
      const typeLabel = COPY_TYPES.find((type) => type.key === copyType)?.value || 'Instagram';

      // Build character-focused instructions if characters are selected
      let fullExtraInstructions = copyExtraInstructions || '';
      if (selectedCharacters.length > 0) {
        const charBlock = selectedCharacters
          .map((c) => `"${c.name}": ${c.description.slice(0, 200)}`)
          .join('\n');
        fullExtraInstructions = `${fullExtraInstructions}\n\nFocus the copy on these characters:\n${charBlock}`.trim();
      }

      const result = await generateMarketingCopyAction({
        projectId: safeProjectId,
        copyType: typeLabel,
        tone: copyTone,
        campaign: copyCampaign,
        language: i18n.language || 'en',
        extraInstructions: fullExtraInstructions || undefined,
        storyContext: selectedContextContent || undefined,
        storyBible: storyBibleContent || undefined,
        characterSummaries: characterSummaries || undefined,
        worldBuilding: worldBuildingContent || undefined,
      });

      const variants = (result.variants || []).map((v, i) => ({
        id: `var-${Date.now()}-${i}`,
        content: v.content,
        hook: v.hook,
        type: typeLabel,
        tone: copyTone,
        campaign: copyCampaign,
        timestamp: Date.now(),
        saved: false,
      }));

      setGeneratedVariants(variants);
      setSelectedVariant(variants[0] || null);
      setSuccessMessage(t('marketing.variantsGenerated'));
    } catch (err) {
      const msg = err?.data?.message || err?.message || t('marketing.failedToGenerate');
      setErrorMessage(msg);
    } finally {
      setIsGeneratingCopy(false);
    }
  };

  const saveCopyVariant = async (variant) => {
    if (!safeProjectId) return;
    try {
      await saveCopyAssetMutation({
        projectId: safeProjectId,
        content: variant.content,
        copyType: variant.type,
        tone: variant.tone,
        campaign: variant.campaign,
      });
      setGeneratedVariants((prev) =>
        prev.map((v) => v.id === variant.id ? { ...v, saved: true } : v)
      );
      setSuccessMessage(t('marketing.copySaved'));
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      setErrorMessage(err.message || t('marketing.failedToSave'));
    }
  };

  const copyCopyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccessMessage(t('common.copiedToClipboard'));
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch { /* ignore */ }
  };

  const startEditingCopy = (asset) => {
    setEditingCopyId(asset.id);
    setEditingCopyText(asset.content);
  };

  const saveEditedCopy = async (assetId) => {
    try {
      await updateCopyContentMutation({ assetId, content: editingCopyText });
      setEditingCopyId(null);
      setEditingCopyText('');
      setSuccessMessage(t('marketing.copyUpdated'));
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      setErrorMessage(err.message || t('marketing.failedToUpdate'));
    }
  };

  const cancelEditingCopy = () => {
    setEditingCopyId(null);
    setEditingCopyText('');
  };

  const generateVisualConcept = async () => {
    if (!safeProjectId || isGeneratingVisual || !visualPrompt.trim()) return;
    setIsGeneratingVisual(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const styleLabel = ART_STYLES.find((style) => style.key === artStyle)?.value || 'Cinematic';

      // Build enriched prompt with character descriptions
      let enrichedPrompt = visualPrompt.trim();
      if (selectedCharacters.length > 0) {
        const charBlock = selectedCharacters
          .map((c) => `Character "${c.name}": ${c.description.slice(0, 150)}`)
          .join('\n');
        enrichedPrompt = `${enrichedPrompt}\n\nFeatured characters:\n${charBlock}`;
      }

      // Combine story context with any additional character context
      let combinedContext = selectedContextContent || '';
      if (selectedCharacters.length > 0 && !combinedContext) {
        combinedContext = selectedCharacters.map((c) => c.description).join('\n\n');
      }

      const result = await generateVisualImageAction({
        projectId: safeProjectId,
        prompt: enrichedPrompt,
        artStyle: styleLabel,
        aspectRatio,
        engine: genEngine,
        storyContext: combinedContext || undefined,
      });

      // Persist to Convex
      await saveVisualAssetMutation({
        projectId: safeProjectId,
        imageUrl: result.imageUrl,
        imageStorageId: result.storageId || undefined,
        prompt: visualPrompt.trim(),
        artStyle: styleLabel,
        aspectRatio,
        model: result.model,
        engine: genEngine,
      });

      setSuccessMessage(t('marketing.imageGenerated'));
      // Keep prompt so users can iterate — don't clear it
    } catch (err) {
      const msg = err?.data?.message || err?.message || t('marketing.failedToGenerateImage');
      setErrorMessage(msg);
    } finally {
      setIsGeneratingVisual(false);
    }
  };

  /* Guards */
  if (!safeProjectId) {
    return (
      <main className="mkt-studio loading">
        <div className="mkt-empty-center">{t('common.invalidProjectLink')} <button className="btn btn-secondary" onClick={() => navigate('/projects')}>{t('common.back')}</button></div>
      </main>
    );
  }
  if (project === undefined || files === undefined) {
    return <main className="mkt-studio loading"><div className="mkt-empty-center">{t('marketing.loadingStudio')}</div></main>;
  }
  if (!project) {
    return <main className="mkt-studio loading"><div className="mkt-empty-center">{t('common.projectNotFound')} <button className="btn btn-secondary" onClick={() => navigate('/projects')}>{t('common.back')}</button></div></main>;
  }

  const totalAssets = savedCopyAssets.length + generatedImages.length;

  // Combined assets for Asset Library
  const allAssets = useMemo(() => {
    const copyAssets = savedCopyAssets.map((c) => ({ ...c, assetType: 'copy' }));
    const visualAssets = generatedImages.map((img, i) => ({ ...img, id: `img-${img.timestamp}-${i}`, assetType: 'visual' }));
    return [...copyAssets, ...visualAssets].sort((a, b) => (b.timestamp || b.savedAt || 0) - (a.timestamp || a.savedAt || 0));
  }, [savedCopyAssets, generatedImages]);

  const filteredAssets = useMemo(() => {
    let items = allAssets;
    if (libraryTypeFilter === 'copy') items = items.filter((a) => a.assetType === 'copy');
    if (libraryTypeFilter === 'visuals') items = items.filter((a) => a.assetType === 'visual');
    if (libraryCampaignFilter !== 'All') items = items.filter((a) => a.campaign === libraryCampaignFilter || a.assetType === 'visual');
    return items;
  }, [allAssets, libraryTypeFilter, libraryCampaignFilter]);

  /* ═══════ Render ═══════ */
  return (
    <main className="mkt-studio" aria-label={t('marketing.title')}>

      {/* ── Header ── */}
      <header className="mkt-header">
        <div className="mkt-header-icon">📣</div>
        <h1>{t('marketing.title')}</h1>
        <div className="mkt-header-actions">
          <button className="btn btn-secondary" onClick={() => navigate(`/projects/${safeProjectId}`)}>{t('common.back')}</button>
          <button className="btn btn-secondary" onClick={() => navigate(`/projects/${safeProjectId}/editor`)}>{t('common.editor')}</button>
        </div>
      </header>

      {/* ── Tabs ── */}
      <nav className="mkt-tabs" role="tablist">
        {TABS.map((tab) => (
          <button key={tab} role="tab" aria-selected={activeTab === tab} className={`mkt-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
            <span className="mkt-tab-icon">{TAB_CONFIG[tab].icon}</span>
            {t(TAB_CONFIG[tab].labelKey)}
          </button>
        ))}
      </nav>

      {/* ── Alerts ── */}
      {errorMessage ? <div className="mkt-alert error">{errorMessage}</div> : null}
      {successMessage ? <div className="mkt-alert success">{successMessage}</div> : null}

      {/* ── Surface ── */}
      <section className="mkt-surface">

        {/* ══ Overview ══ */}
        {activeTab === 'overview' ? (
          <div className="mkt-fade" role="tabpanel" aria-label={t('marketing.tabs.overview')}>
            <h2 className="mkt-overview-title">{t('marketing.overviewTitle')}</h2>
            <p className="mkt-overview-desc">{t('marketing.overviewDesc')}</p>

            {/* Stats */}
            <div className="mkt-stats-row">
              <div className="mkt-stat-card">
                <span className="stat-number">{totalAssets}</span>
                <span className="stat-label">{t('marketing.totalAssets')}</span>
              </div>
              <div className="mkt-stat-card">
                <span className="stat-number">{savedCopyAssets.length}</span>
                <span className="stat-label">{t('marketing.copyAssets')}</span>
              </div>
              <div className="mkt-stat-card">
                <span className="stat-number">{generatedImages.length}</span>
                <span className="stat-label">{t('marketing.visualAssets')}</span>
              </div>
              <div className="mkt-stat-card">
                <span className="stat-number">{generatedVariants.length}</span>
                <span className="stat-label">{t('marketing.variantsLabel')}</span>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mkt-actions-row">
              <button className="mkt-action-card" onClick={() => setActiveTab('copy-studio')}>
                <div className="mkt-action-icon">✍️</div>
                <div className="mkt-action-text">
                  <strong>{t('marketing.quickActions.generateCopyTitle')}</strong>
                  <span>{t('marketing.quickActions.generateCopyDesc')}</span>
                </div>
                <span className="mkt-action-arrow">›</span>
              </button>
              <button className="mkt-action-card" onClick={() => setActiveTab('visual-studio')}>
                <div className="mkt-action-icon">⚡</div>
                <div className="mkt-action-text">
                  <strong>{t('marketing.quickActions.generateVisualsTitle')}</strong>
                  <span>{t('marketing.quickActions.generateVisualsDesc')}</span>
                </div>
                <span className="mkt-action-arrow">›</span>
              </button>
              <button className="mkt-action-card" onClick={() => setActiveTab('asset-library')}>
                <div className="mkt-action-icon">📁</div>
                <div className="mkt-action-text">
                  <strong>{t('marketing.quickActions.assetLibraryTitle')}</strong>
                  <span>{t('marketing.quickActions.assetLibraryDesc')}</span>
                </div>
                <span className="mkt-action-arrow">›</span>
              </button>
              <button className="mkt-action-card" onClick={() => setActiveTab('compose')}>
                <div className="mkt-action-icon">🧩</div>
                <div className="mkt-action-text">
                  <strong>{t('marketing.quickActions.composePostTitle')}</strong>
                  <span>{t('marketing.quickActions.composePostDesc')}</span>
                </div>
                <span className="mkt-action-arrow">›</span>
              </button>
            </div>

            {/* Empty state / Recent assets */}
            {totalAssets === 0 && (
              <div className="mkt-empty-state">
                <span className="mkt-empty-icon">📦</span>
                <h3>{t('marketing.noAssetsYetTitle')}</h3>
                <p>{t('marketing.noAssetsYetDesc')}</p>
                <div className="mkt-empty-actions">
                  <button className="btn btn-primary" onClick={() => setActiveTab('copy-studio')}>{t('marketing.quickActions.generateCopyTitle')}</button>
                  <button className="btn btn-secondary" onClick={() => setActiveTab('visual-studio')}>{t('marketing.createVisual')}</button>
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* ══ Copy Studio — Redesigned ══ */}
        {activeTab === 'copy-studio' ? (
          <div className="mkt-fade" role="tabpanel" aria-label={t('marketing.tabs.copyStudio')}>
            <div className="cs-header">
              <h2 className="cs-title">{t('marketing.tabs.copyStudio')}</h2>
              <p className="cs-subtitle">{t('marketing.copyStudioSubtitle')}</p>
            </div>

            {/* Generator Card */}
            <div className="cs-generator">
              {/* Copy Type chips */}
              <div className="cs-section">
                <span className="cs-label">{t('marketing.copyTypeLabel')}</span>
                <div className="cs-type-chips">
                  {COPY_TYPES.map((typeOption) => (
                    <button
                      key={typeOption.key}
                      className={`cs-type-chip ${copyType === typeOption.key ? 'active' : ''}`}
                      onClick={() => setCopyType(typeOption.key)}
                    >
                      <span className="cs-type-icon">{typeOption.icon}</span>
                      {t(typeOption.labelKey)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tone + Campaign row */}
              <div className="cs-row">
                <div className="cs-section cs-tone-section">
                  <span className="cs-label">{t('marketing.toneLabel')}</span>
                  <div className="cs-tone-pills">
                    {TONE_OPTIONS.map((tone) => (
                      <button
                        key={tone.value}
                        className={`cs-tone-pill ${copyTone === tone.value ? 'active' : ''}`}
                        onClick={() => setCopyTone(tone.value)}
                      >
                        {t(tone.labelKey)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="cs-section cs-campaign-section">
                  <span className="cs-label">{t('marketing.campaignLabel')}</span>
                  <select className="cs-select" value={copyCampaign} onChange={(e) => setCopyCampaign(e.target.value)}>
                    {CAMPAIGN_OPTIONS.filter((campaign) => campaign.value !== 'All').map((campaign) => (
                      <option key={campaign.value} value={campaign.value}>{t(campaign.labelKey)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Auto-attached context indicator */}
              <div className="cs-section">
                <span className="cs-label">{t('marketing.autoAttachedContext')}</span>
                <div className="cs-auto-context-tags">
                  {storyBibleContent ? <span className="cs-context-tag cs-auto-tag">✦ {t('marketing.storyBibleLabel')}</span> : null}
                  {characterSummaries ? <span className="cs-context-tag cs-auto-tag">♦ {t('sidebar.systemFolders.characters')}</span> : null}
                  {worldBuildingContent ? <span className="cs-context-tag cs-auto-tag">☉ {t('sidebar.systemFolders.worldBuilding')}</span> : null}
                  {!storyBibleContent && !characterSummaries && !worldBuildingContent ? (
                    <span className="cs-context-hint">{t('marketing.autoContextEmpty')}</span>
                  ) : null}
                </div>
              </div>

              {/* Character Selection */}
              {characters.length > 0 && (
                <div className="cs-section">
                  <span className="cs-label">{t('marketing.featuredCharacters')}</span>
                  <div className="vs-char-chips">
                    {characters.map((char) => (
                      <button
                        key={char.name}
                        className={`vs-char-chip ${selectedCharacters.find((c) => c.name === char.name) ? 'active' : ''}`}
                        onClick={() => toggleCharacter(char)}
                        title={char.description.slice(0, 100)}
                      >
                        <span className="vs-char-avatar">{char.name[0]}</span>
                        {char.name}
                      </button>
                    ))}
                  </div>
                  {selectedCharacters.length > 0 && (
                    <div className="vs-char-active-note">
                      {t('marketing.selectedCharactersNote', { count: selectedCharacters.length })}
                    </div>
                  )}
                </div>
              )}

              {/* Story Context Reference */}
              <div className="cs-section">
                <span className="cs-label">{t('marketing.additionalContextFile')}</span>
                <select className="cs-select" value={storyContext} onChange={(e) => setStoryContext(e.target.value)}>
                  <option value="">{t('marketing.noAdditionalContext')}</option>
                  {storyContextOptions.map((opt) => (
                    <option key={opt.path} value={opt.path}>{opt.icon} {opt.label}</option>
                  ))}
                </select>
                {storyContext && selectedContextContent ? (
                  <div className="cs-context-attached">
                    <span className="cs-context-tag">{t('marketing.attached')}</span>
                    <p className="cs-context-preview">{selectedContextContent.slice(0, 140)}...</p>
                  </div>
                ) : null}
              </div>

              {/* Extra Instructions */}
              <div className="cs-section">
                <span className="cs-label">{t('marketing.extraInstructions')}</span>
                <textarea
                  className="cs-instructions"
                  rows={2}
                  placeholder={t('marketing.extraInstructionsPlaceholder')}
                  value={copyExtraInstructions}
                  onChange={(e) => setCopyExtraInstructions(e.target.value)}
                />
              </div>

              <button className="cs-generate-btn" onClick={generateCopyVariants} disabled={isGeneratingCopy}>
                {isGeneratingCopy ? (
                  <><span className="vs-gen-spinner" /> {t('marketing.generating')}</>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                    {t('marketing.generateVariants')}
                  </>
                )}
              </button>
            </div>

            {/* Generated Variants */}
            {generatedVariants.length > 0 && (
              <div className="cs-variants-section">
                <div className="cs-variants-header">
                  <h3>{t('marketing.generatedVariantsCount', { count: generatedVariants.length })}</h3>
                  <button className="cs-regenerate-btn" onClick={generateCopyVariants} disabled={isGeneratingCopy}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
                    {t('marketing.regenerate')}
                  </button>
                </div>
                <div className="cs-variants-grid">
                  {generatedVariants.map((variant, i) => (
                    <div
                      key={variant.id}
                      className={`cs-variant-card ${selectedVariant?.id === variant.id ? 'selected' : ''}`}
                      onClick={() => setSelectedVariant(variant)}
                    >
                      <div className="cs-variant-header">
                        <span className="cs-variant-label">{t('marketing.variantLabel', { number: i + 1 })}</span>
                        {variant.saved && <span className="cs-variant-saved-badge">{t('marketing.savedLabel')}</span>}
                      </div>
                      <p className="cs-variant-content">{variant.content}</p>
                      <div className="cs-variant-actions">
                        <button className="cs-variant-btn save" onClick={(e) => { e.stopPropagation(); saveCopyVariant(variant); }} disabled={variant.saved}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/></svg>
                          {t('common.save')}
                        </button>
                        <button className="cs-variant-btn copy" onClick={(e) => { e.stopPropagation(); copyCopyToClipboard(variant.content); }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                          {t('marketing.copyText')}
                        </button>
                        <button className="cs-variant-btn share" onClick={(e) => { e.stopPropagation(); navigate('/projects/community', { state: { prefill: { body: variant.content, linkedProjectId: safeProjectId } } }); }}>
                          {t('community.shareToCommunity')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Preview Mockup */}
            {selectedVariant && (
              <div className="cs-preview-section">
                <div className="cs-preview-tabs">
                  <span className="cs-preview-label">{t('marketing.previewLabel')}</span>
                  {PREVIEW_TABS.map((tab) => (
                    <button
                      key={tab.key}
                      className={`cs-preview-tab ${previewTab === tab.key ? 'active' : ''}`}
                      onClick={() => setPreviewTab(tab.key)}
                    >
                      {tab.icon} {t(tab.labelKey)}
                    </button>
                  ))}
                </div>
                <div className="cs-preview-canvas">
                  {previewTab === 'ig' && (
                    <div className="cs-preview-ig">
                      <div className="cs-ig-header">
                        <div className="cs-ig-avatar" />
                        <div className="cs-ig-user">
                          <strong>{t('marketing.previewProfile')}</strong>
                          <span>{t('marketing.previewAuthor')}</span>
                        </div>
                      </div>
                      <div className="cs-ig-image-placeholder">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                      </div>
                      <div className="cs-ig-caption">{selectedVariant.content}</div>
                    </div>
                  )}
                  {previewTab === 'tw' && (
                    <div className="cs-preview-tw">
                      <div className="cs-tw-header">
                        <div className="cs-tw-avatar" />
                        <div className="cs-tw-user">
                          <strong>@{t('marketing.previewProfile')}</strong>
                          <span>{t('marketing.previewAuthor')}</span>
                        </div>
                      </div>
                      <div className="cs-tw-content">{selectedVariant.content}</div>
                      <div className="cs-tw-meta">
                        {t('marketing.charactersCount', { count: selectedVariant.content.length })}
                      </div>
                    </div>
                  )}
                  {previewTab === 'email' && (
                    <div className="cs-preview-email">
                      <div className="cs-email-subject">
                        <span>{t('marketing.emailSubjectLabel')}</span> {t('marketing.newReleaseSubject', { title: project?.title || t('marketing.yourBook') })}
                      </div>
                      <div className="cs-email-body">{selectedVariant.content}</div>
                      <div className="cs-email-cta">{t('marketing.readMore')}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* ══ Visual Studio — Redesigned ══ */}
        {activeTab === 'visual-studio' ? (
          <div className="mkt-fade vs-atelier" role="tabpanel" aria-label={t('marketing.tabs.visualStudio')}>

            {/* ── Atelier Header ── */}
            <div className="vs-atelier-header">
              <div className="vs-atelier-title-block">
                <h2 className="vs-atelier-title">{t('marketing.tabs.visualStudio')}</h2>
                <span className="vs-atelier-rule" />
                <p className="vs-atelier-subtitle">{t('marketing.visualStudioSubtitle')}</p>
              </div>
              <div className="vs-engine-toggle">
                <button className={`vs-engine-chip ${genEngine === 'quick' ? 'active' : ''}`} onClick={() => setGenEngine('quick')}>
                  <span className="vs-engine-dot quick" />{t('marketing.engine.quick')} <span className="vs-engine-cost">3</span>
                </button>
                <button className={`vs-engine-chip ${genEngine === 'studio' ? 'active' : ''}`} onClick={() => setGenEngine('studio')}>
                  <span className="vs-engine-dot studio" />{t('marketing.engine.quality')} <span className="vs-engine-cost">8</span>
                </button>
              </div>
            </div>

            <div className="vs-atelier-layout">
              {/* ── Left: Tool Palette ── */}
              <div className="vs-palette">

                {/* Style */}
                <div className="vs-palette-group">
                  <span className="vs-palette-label">{t('marketing.styleLabel')}</span>
                  <div className="vs-style-mosaic">
                    {ART_STYLES.map((s) => (
                      <button key={s.key} className={`vs-mosaic-tile ${artStyle === s.key ? 'active' : ''}`} onClick={() => setArtStyle(s.key)}>
                        <span className="vs-mosaic-name">{t(s.labelKey)}</span>
                        <span className="vs-mosaic-desc">{t(s.descKey)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Format */}
                <div className="vs-palette-group">
                  <span className="vs-palette-label">{t('marketing.formatLabel')}</span>
                  <div className="vs-format-row">
                    {ASPECT_RATIOS.map((r) => (
                      <button key={r.key} className={`vs-format-btn ${aspectRatio === r.key ? 'active' : ''}`} onClick={() => setAspectRatio(r.key)}>
                        <span className="vs-format-ratio">{r.label}</span>
                        <span className="vs-format-desc">{t(r.descKey)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Characters */}
                {characters.length > 0 && (
                  <div className="vs-palette-group">
                    <span className="vs-palette-label">{t('marketing.charactersLabel')}</span>
                    <div className="vs-char-chips">
                      {characters.map((char) => (
                        <button
                          key={char.name}
                          className={`vs-char-chip ${selectedCharacters.find((c) => c.name === char.name) ? 'active' : ''}`}
                          onClick={() => toggleCharacter(char)}
                          title={char.description.slice(0, 100)}
                        >
                          <span className="vs-char-avatar">{char.name[0]}</span>
                          {char.name}
                        </button>
                      ))}
                    </div>
                    {selectedCharacters.length > 0 && (
                      <div className="vs-char-active-note">
                        {t('marketing.visualCharactersNote', { count: selectedCharacters.length })}
                      </div>
                    )}
                  </div>
                )}

                {/* Story Context */}
                <div className="vs-palette-group">
                  <span className="vs-palette-label">{t('marketing.referenceLabel')}</span>
                  <select className="vs-context-select" value={storyContext} onChange={(e) => setStoryContext(e.target.value)}>
                    <option value="">{t('marketing.noStoryContext')}</option>
                    {storyContextOptions.map((opt) => (
                      <option key={opt.path} value={opt.path}>{opt.icon} {opt.label}</option>
                    ))}
                  </select>
                  {storyContext && selectedContextContent ? (
                    <div className="vs-context-attached">
                      <span className="vs-context-attached-tag">{t('marketing.attached')}</span>
                      <p>{selectedContextContent.slice(0, 140)}...</p>
                    </div>
                  ) : null}
                </div>

                {/* Prompt + Generate */}
                <div className="vs-palette-group vs-prompt-group">
                  <span className="vs-palette-label">{t('marketing.promptLabel')}</span>
                  <textarea
                    className="vs-prompt-input"
                    rows={4}
                    placeholder={t('marketing.visualPromptPlaceholder')}
                    value={visualPrompt}
                    onChange={(e) => setVisualPrompt(e.target.value)}
                  />
                  <button
                    className="vs-generate-btn"
                    onClick={generateVisualConcept}
                    disabled={isGeneratingVisual || !visualPrompt.trim()}
                  >
                    {isGeneratingVisual ? (
                      <>
                        <span className="vs-gen-spinner" />
                        {t('marketing.generatingImage')}
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                        </svg>
                        {t('marketing.generateCredits', { credits: genEngine === 'quick' ? 3 : 8 })}
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* ── Right: Canvas ── */}
              <div className="vs-canvas">
                {/* Main Preview */}
                <div className="vs-canvas-main">
                  {isGeneratingVisual ? (
                    <div className="vs-canvas-loading">
                      <div className="vs-canvas-loader">
                        <div className="vs-loader-ring" />
                        <div className="vs-loader-ring vs-loader-ring-2" />
                      </div>
                      <p className="vs-canvas-loading-text">{t('marketing.creatingImage')}</p>
                      <span className="vs-canvas-loading-sub">{t('marketing.creatingImageDesc')}</span>
                    </div>
                  ) : generatedImages.length > 0 ? (
                    <div className="vs-canvas-image-wrap">
                      <img
                        src={expandedImage?.url || generatedImages[0].url}
                        alt={expandedImage?.prompt || generatedImages[0].prompt}
                        className="vs-canvas-image"
                      />
                      <div className="vs-canvas-image-overlay">
                        <span className="vs-canvas-image-badge">
                          {localizeArtStyle((expandedImage || generatedImages[0]).artStyle)} / {(expandedImage || generatedImages[0]).aspectRatio}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="vs-canvas-empty">
                      <div className="vs-canvas-empty-frame">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" opacity="0.3">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                      </div>
                      <h3 className="vs-canvas-empty-title">{t('marketing.canvasAwaitsTitle')}</h3>
                      <p className="vs-canvas-empty-desc">{t('marketing.canvasAwaitsDesc')}</p>
                    </div>
                  )}
                </div>

                {/* History moved to Asset Library */}
              </div>
            </div>
          </div>
        ) : null}

        {/* ══ Asset Library — Redesigned ══ */}
        {activeTab === 'asset-library' ? (
          <div className="mkt-fade" role="tabpanel" aria-label={t('marketing.tabs.assetLibrary')}>
            <div className="al-layout">
              {/* ── Sidebar ── */}
              <div className="al-sidebar">
                <div className="al-filter-group">
                  <span className="al-filter-label">{t('marketing.typeLabel')}</span>
                  {[
                    { key: 'all', label: t('marketing.allAssetsFilter'), count: allAssets.length },
                    { key: 'copy', label: t('marketing.copyAssets'), count: savedCopyAssets.length },
                    { key: 'visuals', label: t('marketing.visualAssets'), count: generatedImages.length },
                  ].map((f) => (
                    <button
                      key={f.key}
                      className={`al-filter-btn ${libraryTypeFilter === f.key ? 'active' : ''}`}
                      onClick={() => setLibraryTypeFilter(f.key)}
                    >
                      {f.label} <span className="al-filter-count">({f.count})</span>
                    </button>
                  ))}
                </div>

                <div className="al-filter-group">
                  <span className="al-filter-label">{t('marketing.campaignLabel')}</span>
                  {CAMPAIGN_OPTIONS.map((c) => (
                    <button
                      key={c.value}
                      className={`al-filter-btn ${libraryCampaignFilter === c.value ? 'active' : ''}`}
                      onClick={() => setLibraryCampaignFilter(c.value)}
                    >
                      {t(c.labelKey)}
                    </button>
                  ))}
                </div>

                <div className="al-filter-group">
                  <span className="al-filter-label">{t('marketing.viewLabel')}</span>
                  <div className="al-view-toggle">
                    <button className={`al-view-btn ${libraryViewMode === 'grid' ? 'active' : ''}`} onClick={() => setLibraryViewMode('grid')}>{t('marketing.gridView')}</button>
                    <button className={`al-view-btn ${libraryViewMode === 'list' ? 'active' : ''}`} onClick={() => setLibraryViewMode('list')}>{t('marketing.listView')}</button>
                  </div>
                </div>
              </div>

              {/* ── Main Content ── */}
              <div className="al-content">
                {filteredAssets.length === 0 ? (
                  <div className="al-empty">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.3">
                      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0022 16z"/>
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
                    </svg>
                    <p>{t('marketing.noAssetsMatch')}</p>
                  </div>
                ) : (
                  <div className={`al-grid ${libraryViewMode}`}>
                    {filteredAssets.map((asset) => (
                      <div key={asset.id} className={`al-asset-card ${asset.assetType}`}>
                        {asset.assetType === 'visual' ? (
                          <>
                            <div className="al-asset-image-wrap">
                              <img src={asset.url} alt={asset.prompt || ''} className="al-asset-image" />
                              <span className="al-asset-badge">{localizeArtStyle(asset.artStyle)}</span>
                            </div>
                            <div className="al-asset-info">
                              <span className="al-asset-type-tag visual">{t('marketing.visualTag')}</span>
                              <span className="al-asset-ratio">{asset.aspectRatio}</span>
                              <p className="al-asset-prompt">{asset.prompt?.slice(0, 60)}{asset.prompt?.length > 60 ? '...' : ''}</p>
                              <div className="al-asset-info-row">
                                <span className="al-asset-time">{new Date(asset.timestamp).toLocaleString(i18n.language || undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                <button className="al-copy-action-btn share" onClick={(e) => { e.stopPropagation(); navigate('/projects/community', { state: { prefill: { body: asset.prompt || '', imageStorageId: asset.imageStorageId, linkedProjectId: safeProjectId } } }); }}>
                                  {t('community.shareToCommunity')}
                                </button>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className={`al-asset-copy-card ${editingCopyId === asset.id ? 'editing' : ''}`}>
                            <div className="al-asset-copy-header">
                              <span className="al-asset-type-tag copy">{t('marketing.copyTag')}</span>
                              <span className="al-asset-copy-type">{localizeCopyType(asset.copyType || asset.type)}</span>
                              <div className="al-asset-copy-actions">
                                {editingCopyId === asset.id ? (
                                  <>
                                    <button className="al-copy-action-btn save" onClick={() => saveEditedCopy(asset.id)}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                                      {t('common.save')}
                                    </button>
                                    <button className="al-copy-action-btn cancel" onClick={cancelEditingCopy}>{t('common.cancel')}</button>
                                  </>
                                ) : (
                                  <>
                                    <button className="al-copy-action-btn edit" onClick={() => startEditingCopy(asset)}>
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                      {t('common.edit')}
                                    </button>
                                    <button className="al-copy-action-btn copy" onClick={() => copyCopyToClipboard(asset.content)}>
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                                      {t('marketing.copyText')}
                                    </button>
                                    <button className="al-copy-action-btn share" onClick={() => navigate('/projects/community', { state: { prefill: { body: asset.content, linkedProjectId: safeProjectId } } })}>
                                      {t('community.shareToCommunity')}
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            {editingCopyId === asset.id ? (
                              <textarea
                                className="al-asset-copy-editor"
                                value={editingCopyText}
                                onChange={(e) => setEditingCopyText(e.target.value)}
                                autoFocus
                                rows={5}
                              />
                            ) : (
                              <p className="al-asset-copy-text">{asset.content}</p>
                            )}
                            <div className="al-asset-copy-footer">
                              <span className="al-asset-tone">{localizeTone(asset.tone)}</span>
                              <span className="al-asset-time">{new Date(asset.savedAt || asset.timestamp).toLocaleString(i18n.language || undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {/* ══ Compose — Pair copy + visual and share ══ */}
        {activeTab === 'compose' ? (
          <div className="mkt-fade" role="tabpanel" aria-label={t('marketing.compose')}>
            <div className="comp-header">
              <h2 className="comp-title">{t('marketing.quickActions.composePostTitle')}</h2>
              <p className="comp-subtitle">{t('marketing.composeSubtitle')}</p>
            </div>

            <div className="comp-layout">
              {/* Left Panel: Asset Browser */}
              <div className="comp-browser">
                {/* Copy Assets */}
                <div className="comp-browser-section">
                  <span className="comp-browser-label">{t('marketing.copyTag')}</span>
                  {savedCopyAssets.length === 0 ? (
                    <p className="comp-empty-hint">{t('marketing.noSavedCopy')}</p>
                  ) : (
                    <div className="comp-copy-list">
                      {savedCopyAssets.map((asset) => (
                        <button
                          key={asset.id}
                          className={`comp-copy-card ${composeSelectedCopy?.id === asset.id ? 'selected' : ''}`}
                          onClick={() => setComposeSelectedCopy(asset)}
                        >
                          <span className="comp-copy-type">{localizeCopyType(asset.copyType)}</span>
                          <p className="comp-copy-preview">{asset.content?.slice(0, 120)}{asset.content?.length > 120 ? '...' : ''}</p>
                          {asset.tone && <span className="comp-copy-tone">{localizeTone(asset.tone)}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Visual Assets */}
                <div className="comp-browser-section">
                  <span className="comp-browser-label">{t('marketing.visualAssets')}</span>
                  {generatedImages.length === 0 ? (
                    <p className="comp-empty-hint">{t('marketing.noVisualsYet')}</p>
                  ) : (
                    <div className="comp-visual-grid">
                      {generatedImages.map((img) => (
                        <button
                          key={img.id}
                          className={`comp-visual-thumb ${composeSelectedVisual?.id === img.id ? 'selected' : ''}`}
                          onClick={() => setComposeSelectedVisual(img)}
                        >
                          <img src={img.url} alt={img.prompt || ''} className="comp-visual-img" />
                          <span className="comp-visual-style">{localizeArtStyle(img.artStyle)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Panel: Live Preview */}
              <div className="comp-preview">
                {!composeSelectedCopy && !composeSelectedVisual ? (
                  <div className="comp-preview-empty">
                    <div className="comp-preview-empty-frame">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <line x1="8" y1="12" x2="16" y2="12"/>
                        <line x1="12" y1="8" x2="12" y2="16"/>
                      </svg>
                    </div>
                    <h3 className="comp-preview-empty-title">{t('marketing.selectCopyVisualTitle')}</h3>
                    <p className="comp-preview-empty-desc">{t('marketing.selectCopyVisualDesc')}</p>
                  </div>
                ) : (
                  <>
                    <div className="cs-preview-tabs" style={{ padding: '16px 20px 0' }}>
                      <span className="cs-preview-label">{t('marketing.previewLabel')}</span>
                      {PREVIEW_TABS.map((tab) => (
                        <button
                          key={tab.key}
                          className={`cs-preview-tab ${composePreviewTab === tab.key ? 'active' : ''}`}
                          onClick={() => setComposePreviewTab(tab.key)}
                        >
                          {tab.icon} {t(tab.labelKey)}
                        </button>
                      ))}
                    </div>
                    <div className="cs-preview-canvas">
                      {composePreviewTab === 'ig' && (
                        <div className="cs-preview-ig">
                          <div className="cs-ig-header">
                            <div className="cs-ig-avatar" />
                            <div className="cs-ig-user">
                              <strong>{t('marketing.previewProfile')}</strong>
                              <span>{t('marketing.previewAuthor')}</span>
                            </div>
                          </div>
                          {composeSelectedVisual ? (
                            <img src={composeSelectedVisual.url} alt="" className="comp-ig-image" />
                          ) : (
                            <div className="cs-ig-image-placeholder">
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                            </div>
                          )}
                          <div className="cs-ig-caption">{composeSelectedCopy?.content || t('marketing.selectCopyAssetPlaceholder')}</div>
                        </div>
                      )}
                      {composePreviewTab === 'tw' && (
                        <div className="cs-preview-tw">
                          <div className="cs-tw-header">
                            <div className="cs-tw-avatar" />
                            <div className="cs-tw-user">
                              <strong>@{t('marketing.previewProfile')}</strong>
                              <span>{t('marketing.previewAuthor')}</span>
                            </div>
                          </div>
                          <div className="cs-tw-content">{composeSelectedCopy?.content || t('marketing.selectCopyAssetPlaceholder')}</div>
                          {composeSelectedVisual && (
                            <img src={composeSelectedVisual.url} alt="" className="comp-tw-image" />
                          )}
                          <div className="cs-tw-meta">
                            {t('marketing.charactersCount', { count: composeSelectedCopy?.content?.length || 0 })}
                          </div>
                        </div>
                      )}
                      {composePreviewTab === 'email' && (
                        <div className="cs-preview-email">
                          <div className="cs-email-subject">
                            <span>{t('marketing.emailSubjectLabel')}</span> {t('marketing.newReleaseSubject', { title: project?.title || t('marketing.yourBook') })}
                          </div>
                          {composeSelectedVisual && (
                            <img src={composeSelectedVisual.url} alt="" className="comp-email-image" />
                          )}
                          <div className="cs-email-body">{composeSelectedCopy?.content || t('marketing.selectCopyAssetPlaceholder')}</div>
                          <div className="cs-email-cta">{t('marketing.readMore')}</div>
                        </div>
                      )}
                    </div>

                    {/* Share bar */}
                    <div className="comp-share-bar">
                      <button
                        className="comp-share-btn"
                        disabled={!composeSelectedCopy}
                        onClick={() => setShareModal({
                          body: composeSelectedCopy?.content || '',
                          imageStorageId: composeSelectedVisual?.imageStorageId,
                        })}
                      >
                        {t('community.shareToCommunity')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </section>

      {shareModal && (
        <CommunityShareModal
          projectId={safeProjectId}
          initialBody={shareModal.body || ''}
          imageStorageId={shareModal.imageStorageId}
          audioStorageId={shareModal.audioStorageId}
          genre={project?.genre}
          onClose={() => setShareModal(null)}
          onSuccess={(msg) => {
            setSuccessMessage(msg);
            setTimeout(() => setSuccessMessage(''), 3000);
          }}
        />
      )}
    </main>
  );
}

export default MarketingStudioPage;
